/**
 * Rx-12 — paper-Rx digitisation queue.
 *
 * Each row shows who sent the upload, who it is for (relationship, age, gender), how long it has
 * waited and where its review stands. Search, filters, sort and page size sit in a bar fixed above
 * a server-driven virtual list that loads the next page as you scroll (lib/uploadQueue.ts maps the
 * controls onto the API).
 *
 * The filters live in the URL, so the view survives a reload and an upload's page can return to it.
 * Opening a row goes to that upload's own page (DigitiseUpload.tsx: the paper beside the digitise
 * form, Previous / Next through this queue); coming back finds the list where it was left.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { describeError } from "../../api/errors";
import { listDigitiseQueue, type Upload } from "../../api/pharmacist";
import { ExportButton } from "../../components/ui/ExportButton";
import { Select } from "../../components/ui/Select";
import {
  byPageNumber,
  EXPORT_PAGE_SIZE,
  sheetDateTime,
  sheetLabel,
  sheetPhone,
  type ExportColumn,
} from "../../lib/tableExport";
import {
  activeQueueFilterCount,
  clearedQueueFilters,
  FOR_OPTIONS,
  formatDate,
  formatDateTime,
  formatPhone,
  isTodo,
  PER_PAGE_OPTIONS,
  personDetails,
  queueFiltersFromParams,
  queueFiltersToParams,
  REVIEWER_OPTIONS,
  reviewSummary,
  SORT_OPTIONS,
  STATUS_OPTIONS,
  timeAgo,
  toQueueQuery,
  WAIT_TONE,
  waitLevel,
} from "../../lib/uploadQueue";
import type { QueueFilters } from "../../lib/uploadQueue";
import { useSearchBox, useUrlParams } from "../../lib/urlState";
import { useNow } from "../../lib/useNow";
import { TodayCount, UploadStatusBadge } from "./uploadParts";
import { uploadPath, useUploadQueue } from "./uploadQueueData";

const ROW_H = 72; // estimate; rows are measured (mobile rows wrap onto more lines)

// "#1a2b3c4d" is how the queue names an upload.
const EXPORT_COLUMNS: ExportColumn<Upload>[] = [
  { header: "Upload", value: (u) => `#${u.id.slice(0, 8)}` },
  { header: "Uploaded (IST)", value: (u) => sheetDateTime(u.created_at) },
  { header: "Account holder", value: (u) => u.uploaded_by.name },
  { header: "Phone", value: (u) => sheetPhone(u.uploaded_by.phone) },
  { header: "For", value: (u) => (u.for_member ? u.for_member.name : "Account holder") },
  { header: "Relationship", value: (u) => sheetLabel(u.for_member?.relationship) },
  { header: "Age", value: (u) => (u.for_member ?? u.uploaded_by).age },
  { header: "Gender", value: (u) => sheetLabel((u.for_member ?? u.uploaded_by).gender) },
  { header: "Status", value: (u) => sheetLabel(u.status) },
  { header: "Reviewed by", value: (u) => u.reviewed_by_name },
  { header: "Reviewed (IST)", value: (u) => sheetDateTime(u.reviewed_at) },
  { header: "Rx", value: (u) => u.digitized_prescription_ref },
  { header: "Rejection reason", value: (u) => sheetLabel(u.reject_reason) },
  { header: "Rejection note", value: (u) => u.reject_note },
  { header: "Patient's note", value: (u) => u.note },
];
// Desktop columns: Upload · Uploaded by · For · Uploaded · Review · Today.
const COLS =
  "md:grid md:grid-cols-[9rem_minmax(0,1fr)_minmax(0,1fr)_7.5rem_minmax(0,1fr)_4.5rem] md:items-center md:gap-3";

export function Digitise() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const now = useNow();
  const [params, setParams] = useUrlParams();
  const carried = params.toString();
  const effective = useMemo(() => queueFiltersFromParams(new URLSearchParams(carried)), [carried]);
  const query = useMemo(() => toQueueQuery(effective), [effective]);
  const patch = (p: Partial<QueueFilters>) => setParams(queueFiltersToParams(p));
  // The search box types freely; the request follows after a short pause.
  const search = useSearchBox(effective.q, (q) => patch({ q }));
  const clear = () => {
    search.cancel();
    patch(clearedQueueFilters(effective));
  };
  const open = (u: Upload) => navigate(uploadPath(u.id, new URLSearchParams(carried)));

  const [refreshing, setRefreshing] = useState(false);
  const refresh = async () => {
    setRefreshing(true);
    try {
      await qc.refetchQueries({ queryKey: ["ph-uploads"], type: "active" });
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="flex h-full min-h-[30rem] flex-col gap-4">
      <div className="flex shrink-0 flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Digitisation queue</h2>
          <p className="text-sm text-gray-500">Paper prescriptions patients uploaded, to review and digitise.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Every upload the search and filters match, in the queue's order. */}
          <ExportButton
            table="uploads"
            columns={EXPORT_COLUMNS}
            keyOf={(u) => u.id}
            disabled={query === null}
            fetchPage={byPageNumber((page) => listDigitiseQueue({ ...query, page, per_page: EXPORT_PAGE_SIZE }))}
          />
          <button
            type="button"
            onClick={() => void refresh()}
            disabled={refreshing}
            className="rounded-lg border bg-white px-3 py-1.5 text-sm text-gray-700 hover:border-brand hover:text-brand disabled:opacity-50"
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <QueueFilterBar
        filters={effective}
        search={search.text}
        onSearch={search.onChange}
        onChange={patch}
        onClear={clear}
        active={activeQueueFilterCount(effective)}
        rangeInvalid={query === null}
      />

      <QueueList query={query} filtered={activeQueueFilterCount(effective) > 0} onClear={clear} onOpen={open} now={now} />
    </div>
  );
}

// ---------------- Search + filters (fixed above the list) ----------------

function QueueFilterBar({
  filters,
  search,
  onSearch,
  onChange,
  onClear,
  active,
  rangeInvalid,
}: {
  filters: QueueFilters;
  search: string;
  onSearch: (value: string) => void;
  onChange: (patch: Partial<QueueFilters>) => void;
  onClear: () => void;
  active: number;
  rangeInvalid: boolean;
}) {
  // Small screens show the search box and a Filters toggle; from md up everything is inline.
  const [open, setOpen] = useState(false);
  const narrowing = active - (search.trim() ? 1 : 0);

  return (
    <section aria-label="Search and filter uploads" className="shrink-0 rounded-xl border bg-white p-3 text-sm shadow-xs">
      <div className="flex flex-wrap items-end gap-3">
        <Control label="Search" className="min-w-0 flex-1 md:min-w-[16rem]">
          <input
            type="search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Patient, phone, family member, note or #id…"
            aria-label="Search uploads"
            data-testid="upload-search"
            className="w-full rounded-lg border px-3 py-1.5 outline-hidden focus:border-brand focus:ring-1 focus:ring-brand"
          />
        </Control>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls="upload-filters"
          className={`rounded-lg border px-3 py-1.5 md:hidden ${narrowing ? "border-brand text-brand" : "text-gray-600"}`}
        >
          Filters{narrowing ? ` (${narrowing})` : ""}
        </button>

        <div id="upload-filters" className={`${open ? "grid" : "hidden"} w-full grid-cols-2 gap-3 md:contents`}>
          <FilterSelect label="Status" testId="upload-filter-status" value={filters.status} options={STATUS_OPTIONS} onChange={(status) => onChange({ status })} />
          <FilterSelect label="Reviewed by" testId="upload-filter-reviewer" value={filters.reviewer} options={REVIEWER_OPTIONS} onChange={(reviewer) => onChange({ reviewer })} />
          {/* On a phone the two-column grid would cut "Family member" and "Waiting longest" short:
              these two take a whole row, which also keeps the dates side by side. */}
          <FilterSelect label="For" testId="upload-filter-for" value={filters.forWhom} options={FOR_OPTIONS} onChange={(forWhom) => onChange({ forWhom })} className="max-md:col-span-2" />
          <Control label="Uploaded from">
            <input
              type="date"
              value={filters.from}
              max={filters.to || undefined}
              onChange={(e) => onChange({ from: e.target.value })}
              aria-invalid={rangeInvalid}
              className={`rounded-lg border px-2 py-1 ${rangeInvalid ? "border-red-500" : ""}`}
            />
          </Control>
          <Control label="Uploaded to">
            <input
              type="date"
              value={filters.to}
              min={filters.from || undefined}
              onChange={(e) => onChange({ to: e.target.value })}
              aria-invalid={rangeInvalid}
              className={`rounded-lg border px-2 py-1 ${rangeInvalid ? "border-red-500" : ""}`}
            />
          </Control>
          <FilterSelect label="Sort by" testId="upload-sort" value={filters.sort} options={SORT_OPTIONS} onChange={(sort) => onChange({ sort })} className="max-md:col-span-2" />
          <Control label="Per page">
            <Select
              value={filters.perPage}
              options={PER_PAGE_OPTIONS.map((n) => ({ value: n, label: String(n) }))}
              onChange={(perPage) => onChange({ perPage })}
            />
          </Control>
          {active > 0 && (
            <button type="button" onClick={onClear} className="self-end rounded-lg px-3 py-1.5 text-gray-500 hover:bg-gray-100 md:ml-auto">
              Clear filters ({active})
            </button>
          )}
        </div>
      </div>
      {rangeInvalid && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          The “Uploaded from” date is after the “Uploaded to” date.
        </p>
      )}
    </section>
  );
}

// ---------------- The list (virtual scroll, pages fetched on demand) ----------------

// How far each queue view was scrolled, for coming back to it from an upload's page.
const scrollMemory = new Map<string, number>();

function QueueList({
  query,
  filtered,
  onClear,
  onOpen,
  now,
}: {
  query: ReturnType<typeof toQueueQuery>;
  filtered: boolean;
  onClear: () => void;
  onOpen: (upload: Upload) => void;
  now: number;
}) {
  const {
    data,
    error,
    isPending,
    isFetching,
    isPlaceholderData,
    isFetchNextPageError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useUploadQueue(query);

  const rows = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);
  const total = data?.pages[0]?.total ?? 0;

  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_H,
    getItemKey: (i) => rows[i]?.id ?? i,
    overscan: 8,
  });

  const items = virtualizer.getVirtualItems();
  const lastIndex = items.length ? items[items.length - 1].index : -1;
  useEffect(() => {
    // Ask for the next page a few rows before the end, so scrolling rarely waits.
    if (!isPlaceholderData && hasNextPage && !isFetchingNextPage && !isFetchNextPageError && lastIndex >= rows.length - 5) {
      void fetchNextPage();
    }
  }, [lastIndex, rows.length, hasNextPage, isFetchingNextPage, isFetchNextPageError, isPlaceholderData, fetchNextPage]);

  // A new search / filter starts at the top of its results; coming back from an upload's page,
  // the list is where it was left.
  const queryId = JSON.stringify(query);
  useEffect(() => {
    parentRef.current?.scrollTo({ top: scrollMemory.get(queryId) ?? 0 });
  }, [queryId]);

  let body: ReactNode;
  if (query === null) {
    body = <Centered>Pick an “Uploaded from” date on or before the “Uploaded to” date.</Centered>;
  } else if (isPending) {
    body = (
      <div className="flex-1 space-y-2 p-4" aria-busy="true" aria-label="Loading uploads">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-12 animate-pulse rounded-sm bg-gray-100" />
        ))}
      </div>
    );
  } else if (error && rows.length === 0) {
    body = (
      <Centered>
        <span className="text-red-600">{describeError(error)}</span>
        <button type="button" onClick={() => void refetch()} className="text-sm font-medium text-brand hover:underline">
          Try again
        </button>
      </Centered>
    );
  } else if (rows.length === 0) {
    body = filtered ? (
      <Centered>
        <span>No uploads match these filters.</span>
        <button type="button" onClick={onClear} className="text-sm font-medium text-brand hover:underline">
          Clear filters
        </button>
      </Centered>
    ) : (
      <Centered>
        <span className="font-medium text-gray-600">Nothing to review.</span>
        <span>New paper prescriptions from patients will appear here.</span>
      </Centered>
    );
  } else {
    body = (
      // The scroller is absolutely placed in the slot so a long list can't stretch the page:
      // the slot takes the height left under the filter bar, and only the rows scroll.
      <div className="relative min-h-0 flex-1">
        <div
          ref={parentRef}
          onScroll={(e) => scrollMemory.set(queryId, e.currentTarget.scrollTop)}
          className={`absolute inset-0 overflow-auto transition-opacity ${isPlaceholderData ? "opacity-60" : ""}`}
          aria-busy={isPlaceholderData}
        >
          <ul style={{ height: virtualizer.getTotalSize(), position: "relative" }} aria-label="Uploads">
            {items.map((vi) => (
              <li
                key={vi.key}
                ref={virtualizer.measureElement}
                data-index={vi.index}
                className="border-b"
                style={{ position: "absolute", top: 0, left: 0, width: "100%", transform: `translateY(${vi.start}px)` }}
              >
                <QueueRow upload={rows[vi.index]} now={now} onOpen={onOpen} />
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border bg-white shadow-xs">
      <div className={`hidden shrink-0 border-b bg-gray-50 px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-gray-500 ${COLS}`}>
        <span>Upload</span>
        <span>Uploaded by</span>
        <span>For</span>
        <span>Uploaded</span>
        <span>Review</span>
        <span title="Prescriptions digitised for this patient account today, of the daily limit">Today</span>
      </div>

      {body}

      <div className="flex shrink-0 items-center justify-between gap-3 border-t px-4 py-2 text-xs text-gray-500" aria-live="polite">
        <span>{data && query !== null ? `Showing ${rows.length} of ${total}` : ""}</span>
        {isPlaceholderData && isFetching ? (
          <span className="animate-pulse">Updating…</span>
        ) : isFetchingNextPage ? (
          <span className="animate-pulse">Loading more…</span>
        ) : isFetchNextPageError ? (
          <button type="button" onClick={() => void fetchNextPage()} className="font-medium text-red-600 hover:underline">
            Couldn't load more — try again
          </button>
        ) : hasNextPage ? (
          <span>Scroll for more</span>
        ) : (
          rows.length > 0 && <span>End of list</span>
        )}
      </div>
    </div>
  );
}

function QueueRow({ upload: u, now, onOpen }: { upload: Upload; now: number; onOpen: (upload: Upload) => void }) {
  const wait = isTodo(u) ? WAIT_TONE[waitLevel(u.created_at, now)] : "text-gray-600";
  const forDetails = personDetails(u.for_member ?? u.uploaded_by);
  return (
    <button
      type="button"
      onClick={() => onOpen(u)}
      className={`block w-full px-4 py-3 text-left text-sm transition-colors hover:bg-gray-50 focus-visible:bg-brand/5 focus-visible:outline-hidden ${COLS}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs font-semibold text-gray-700">#{u.id.slice(0, 8)}</span>
          <UploadStatusBadge status={u.status} />
          <span className={`ml-auto text-xs md:hidden ${wait}`}>{timeAgo(u.created_at, now)}</span>
        </div>
        {u.note && (
          <div className="mt-1 truncate text-xs italic text-gray-500" title={u.note}>
            “{u.note}”
          </div>
        )}
      </div>
      <div className="mt-2 min-w-0 md:mt-0">
        <div className="truncate font-medium text-gray-800">{u.uploaded_by.name || "Unnamed patient"}</div>
        <div className="truncate text-xs text-gray-500">{u.uploaded_by.phone ? formatPhone(u.uploaded_by.phone) : "No phone"}</div>
      </div>
      <div className="mt-1 min-w-0 md:mt-0">
        <div className="truncate text-gray-700">
          <span className="text-xs text-gray-400 md:hidden">For </span>
          {u.for_member ? u.for_member.name || "Family member" : "Self"}
        </div>
        {forDetails && <div className="truncate text-xs text-gray-500">{forDetails}</div>}
      </div>
      <div className="hidden md:block">
        <div className={wait} title={formatDateTime(u.created_at)}>
          {timeAgo(u.created_at, now)}
        </div>
        <div className="text-xs text-gray-400">{formatDate(u.created_at)}</div>
      </div>
      <div className="mt-1 flex min-w-0 items-center justify-between gap-2 md:mt-0 md:block">
        <span className="min-w-0 truncate text-xs text-gray-500 md:block" title={reviewSummary(u, now)}>
          {reviewSummary(u, now)}
        </span>
        <span className="md:hidden">
          <TodayCount used={u.digitized_today} limit={u.digitize_daily_limit} />
        </span>
      </div>
      <div className="hidden md:block">
        <TodayCount used={u.digitized_today} limit={u.digitize_daily_limit} />
      </div>
    </button>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-sm text-gray-400">{children}</div>;
}

function Control({ label, children, className = "" }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={`flex flex-col gap-1 text-xs text-gray-500 ${className}`}>
      {label}
      {children}
    </label>
  );
}

function FilterSelect<T extends string>({
  label,
  testId,
  value,
  options,
  onChange,
  className,
}: {
  label: string;
  testId: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  className?: string;
}) {
  return (
    <Control label={label} className={className}>
      <Select value={value} options={options} onChange={onChange} data-testid={testId} />
    </Control>
  );
}
